import React from 'react';
import Drawer from '../ui/Drawer';
import Alert from '../ui/Alert';
import Select from '../ui/Select';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import PushFields from '../messaging/PushFields';
import WhatsappFields from '../messaging/WhatsappFields';
import SmsFields from '../messaging/SmsFields';
import EmailFields from '../messaging/EmailFields';
import RcsFields from '../messaging/RcsFields';
import SegmentBuilder from '../audience/SegmentBuilder';
import PropertyFilters from '../audience/PropertyFilters';
import { NODE_DEFS } from '../../data/nodeDefinitions';
import { TIME_WINDOWS } from '../../data/events';
import { segmentService } from '../../services/segmentService';
import styles from './ConfigDrawer.module.css';

function Field({ label, children }) {
  return (
    <div className={styles.field}>
      <label className={styles.label}>{label}</label>
      {children}
    </div>
  );
}

// Every call site elsewhere in this file keeps its {value, onChange, ...}
// contract unchanged — only the rendering underneath moved to the shared
// Input component.
function Inp({ value, onChange, placeholder, type = 'text' }) {
  return (
    <Input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

// Every call site elsewhere in this file keeps its {value, onChange, options}
// contract unchanged — only the rendering underneath moved to the shared
// searchable Select component.
function Sel({ value, onChange, options }) {
  const normalized = options.map(o => (
    typeof o === 'string'
      ? { value: o, label: o }
      : { value: o.value, label: o.label + (o.disabled ? ' (coming soon)' : ''), disabled: o.disabled }
  ));
  return <Select value={value || ''} options={normalized} onChange={onChange} />;
}

function TextArea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <Textarea
      rows={rows}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

class ConfigDrawer extends React.Component {
  constructor(props) {
    super(props);
    this.state = { segments: [], events: [] };
  }

  componentDidMount() {
    segmentService.getAll().then((segments) => this.setState({ segments })).catch(() => {});
    // Same live discovery the Segment Builder's FilterRow.jsx already uses,
    // instead of data/events.js's hardcoded APP_EVENTS list — real event
    // names actually seen in ClickHouse, not dummy seeded ones. Scoped to
    // the plain `events` table specifically (not lead_events/earning_events/
    // task_events) because that's the only table the journey engine's Event
    // Trigger/Goal Check actually query (engine.service.js's
    // eventTriggerUserIds/evaluateGoal) — offering an event from a table the
    // engine can't check yet would silently never fire.
    segmentService.getOptions()
      .then((options) => {
        const seen = new Set();
        const events = (options.events || [])
          .filter((e) => e.table === 'events' && !seen.has(e.name) && seen.add(e.name))
          .map((e) => e.name);
        this.setState({ events });
      })
      .catch(() => {});
  }

  render() {
    const { node, open, onClose, onChange } = this.props;
    if (!node) return null;

    const def = NODE_DEFS[node.type] || {};
    const cfg = node.cfg || {};
    const set = key => val => onChange({ ...cfg, [key]: val });
    const onFieldChange = (field, val) => onChange({ ...cfg, [field]: val });

    const fields = this._fields(node.type, cfg, set, onFieldChange, this.state.segments);

    return (
      <Drawer
        open={open}
        onClose={onClose}
        title={def.label}
        subtitle="Configure this step"
        ico={def.ico}
        icoStyle={{ background: def.icoC, color: def.icoT }}
        width={node.type === 'entry-segment' ? '520px' : '360px'}
        footer={
          <button className={styles.saveBtn} onClick={onClose}>
            Done
          </button>
        }
      >
        <div className={styles.body}>
          {def.comingSoon && (
            <div style={{ marginBottom: 16 }}>
              <Alert type="warning" title="Not run by the journey engine yet">
                This step type is here for planning — the journey engine doesn't execute it yet. It'll be skipped when you publish.
              </Alert>
            </div>
          )}
          {fields}
        </div>
      </Drawer>
    );
  }

  _fields(type, cfg, set, onFieldChange, segments) {
    const { onChange, node } = this.props;
    switch (type) {
      case 'entry-event':
        return (
          <>
            <Field label="Trigger Event">
              <Sel value={cfg.event} onChange={set('event')} options={this.state.events} />
            </Field>
            <Field label="Lookback window">
              <div className={styles.row2}>
                <Inp value={cfg.lookbackValue} onChange={set('lookbackValue')} placeholder="7" type="number" />
                <Sel value={cfg.lookbackUnit} onChange={set('lookbackUnit')} options={['hours','days','weeks','months']} />
              </div>
              <div className={styles.hint}>Enrolls users who fired this event within the window, checked every 15 min. Expect up to ~20 min from event to enrollment.</div>
            </Field>
            <Field label="Event properties (optional)">
              <PropertyFilters
                propFilters={cfg.propFilters || []}
                onChange={set('propFilters')}
                eventName={cfg.event}
                eventTable="events"
                idPrefix={node.id}
              />
              <div className={styles.hint}>Enforced by the journey engine — only users whose event matched every condition are enrolled.</div>
            </Field>
          </>
        );

      case 'entry-schedule':
        return (
          <>
            <Field label="Cron Expression">
              <Inp value={cfg.cron} onChange={set('cron')} placeholder="0 18 * * *" />
              <div className={styles.hint}>Checked every 15 min — schedules finer than that won't fire on every intended tick.</div>
            </Field>
            <Field label="Timezone">
              <Sel value={cfg.tz} onChange={set('tz')} options={['IST +5:30','UTC','EST −5','PST −8']} />
            </Field>
            <Field label="Segment">
              <Sel
                value={cfg.segmentId}
                onChange={set('segmentId')}
                options={segments.map(s => ({ value: s.id, label: s.name }))}
              />
              <div className={styles.hint}>Evaluated fresh each time the schedule fires.</div>
            </Field>
          </>
        );

      case 'entry-segment':
        return (
          <Field label="Segment">
            {/* Same component Campaigns' audience step uses — its own
                Saved Segment / Build Filter toggle covers both "just pick
                one" and "define custom conditions" without us needing two
                separate UIs here. Picking a saved segment updates
                segmentId/segmentName immediately, the same as the plain
                dropdown this replaced; building a filter only commits once
                explicitly saved (via the component's own Save bar) — that's
                what turns it into a real, reusable segment_id. */}
            <SegmentBuilder
              onChange={(audience) => {
                if (audience.mode === 'saved' && audience.selectedSegId) {
                  const seg = segments.find(s => s.id === audience.selectedSegId);
                  onChange({ ...cfg, segmentId: audience.selectedSegId, segmentName: seg?.name || '' });
                }
              }}
              onSaved={(segment) => onChange({ ...cfg, segmentId: segment.id, segmentName: segment.name })}
            />
          </Field>
        );

      case 'entry-api':
        return (
          <>
            <Field label="Description">
              <Inp value={cfg.description} onChange={set('description')} placeholder="e.g. External CRM trigger" />
            </Field>
            <Field label="Webhook Secret">
              <Inp value={cfg.secret} onChange={set('secret')} placeholder="Shared secret for callers to sign requests" />
            </Field>
          </>
        );

      case 'action-push':
        return <PushFields {...cfg} onChange={onFieldChange} showPreview={false} />;

      case 'action-whatsapp':
        return <WhatsappFields {...cfg} onChange={onFieldChange} />;

      case 'action-email':
        return <EmailFields {...cfg} onChange={onFieldChange} />;

      case 'action-sms':
        return <SmsFields {...cfg} onChange={onFieldChange} />;

      case 'action-rcs':
        return <RcsFields {...cfg} onChange={onFieldChange} />;

      case 'action-webhook':
        return (
          <>
            <Field label="URL">
              <Inp value={cfg.url} onChange={set('url')} placeholder="https://api.example.com/…" />
            </Field>
            <Field label="Method">
              <Sel value={cfg.method} onChange={set('method')} options={['POST','GET','PUT','PATCH']} />
            </Field>
            <Field label="Payload (JSON)">
              <TextArea value={cfg.payload} onChange={set('payload')} placeholder='{"event":"…"}' rows={4} />
            </Field>
          </>
        );

      case 'wait-relative':
        return (
          <>
            <Field label="Duration">
              <div className={styles.row2}>
                <Inp value={cfg.amount} onChange={set('amount')} placeholder="1" type="number" />
                <Sel value={cfg.unit} onChange={set('unit')} options={['minutes','hours','days','weeks']} />
              </div>
            </Field>
          </>
        );

      case 'wait-absolute':
        return (
          <>
            <Field label="Rule">
              <Sel value={cfg.rule} onChange={set('rule')} options={[
                { value: 'next_day_noon',  label: 'Next day 12 PM' },
                { value: 'next_monday',    label: 'Next Monday 9 AM' },
                { value: 'end_of_week',    label: 'End of week' },
                { value: 'custom',         label: 'Custom date/time' },
              ]} />
            </Field>
            {cfg.rule === 'custom' && (
              <Field label="Wait until">
                <div className={styles.row2}>
                  <Inp value={cfg.customDate} onChange={set('customDate')} type="date" />
                  <Inp value={cfg.customTime} onChange={set('customTime')} type="time" />
                </div>
              </Field>
            )}
          </>
        );

      case 'logic-condition': {
        const isDeliveryCheck = cfg.condType === 'if_delivered' || cfg.condType === 'if_failed';
        return (
          <>
            <Field label="Condition">
              <Sel value={cfg.condType} onChange={set('condType')} options={[
                { value: 'if_not_opened',       label: "Hasn't opened previous step" },
                { value: 'if_delivered',        label: 'Previous step delivered' },
                { value: 'if_failed',           label: 'Previous step failed to send' },
                { value: 'event_performed',     label: 'Event performed', disabled: true },
                { value: 'event_not_performed', label: 'Event NOT performed', disabled: true },
                { value: 'user_property',       label: 'User property', disabled: true },
              ]} />
              {isDeliveryCheck && (
                <div className={styles.hint}>
                  Must directly follow an action step (Push, SMS, Email, WhatsApp, or RCS) — checks that step's own send outcome, no event/time-window setup needed.
                  {cfg.condType === 'if_delivered'
                    ? ' Only Email confirms real delivery today; WhatsApp and Push have no delivery webhook yet and will never resolve "delivered." Add a wait before this step to give slower channels (SMS, RCS) time to report.'
                    : ' Reliable immediately for every channel — a send that fails at the API level (bad config, no contact info) is known right away, no wait needed.'}
                </div>
              )}
              {!isDeliveryCheck && (
                <div className={styles.hint}>Only "Hasn't opened previous step" and the two delivery-outcome options above run today — the rest are planned but not yet evaluated by the journey engine.</div>
              )}
            </Field>
            {!isDeliveryCheck && (
              <>
                <Field label="Event">
                  <Sel value={cfg.event} onChange={set('event')} options={this.state.events} />
                </Field>
                <Field label="Time Window">
                  <Sel value={cfg.timeWindow} onChange={set('timeWindow')} options={TIME_WINDOWS.map(tw => tw.label)} />
                  <div className={styles.hint}>Not yet enforced by the engine — the check runs whenever this journey's cron tick reaches this step.</div>
                </Field>
              </>
            )}
          </>
        );
      }

      case 'logic-ab':
        return (
          <>
            <Field label="Variant A %">
              <Inp value={cfg.splitA} onChange={set('splitA')} placeholder="50" type="number" />
            </Field>
            <Field label="Variant B %">
              <Inp value={cfg.splitB} onChange={set('splitB')} placeholder="50" type="number" />
            </Field>
          </>
        );

      case 'logic-goal':
        return (
          <Field label="Goal Event">
            <Sel value={cfg.event} onChange={set('event')} options={this.state.events} />
          </Field>
        );

      case 'logic-exit':
        return (
          <Field label="Exit Reason">
            <Sel value={cfg.reason} onChange={set('reason')} options={[
              { value: 'journey_complete', label: 'Journey complete' },
              { value: 'goal_achieved',    label: 'Goal achieved' },
              { value: 'unsubscribed',     label: 'User unsubscribed' },
            ]} />
          </Field>
        );

      default:
        return <div className={styles.noConfig}>No configuration needed.</div>;
    }
  }
}

export default ConfigDrawer;
